import React, { useState } from "react";
import { motion } from "framer-motion";
import "./DataUpload.css";

function DataUpload({ updateDataStatus, dataStatus }) {
  const [uploadStates, setUploadStates] = useState({
    sales: { loading: false, message: "", error: "" },
    costs: { loading: false, message: "", error: "" },
    raws: { loading: false, message: "", error: "" },
  });

  const uploadConfigs = [
    {
      id: "sales",
      title: "Sales Data",
      icon: "📊",
      endpoint: "/upload/sales-data",
      description: "Upload your sales transaction data (CSV/Excel)",
      requiredColumns: [
        "order_id",
        "product_name",
        "quantity",
        "price",
        "order_date",
      ],
      sampleData:
        "order_id,product_name,quantity,price,order_date\n1001,Pizza Margherita,2,12.99,2024-01-15",
    },
    {
      id: "costs",
      title: "Product Costs",
      icon: "💰",
      endpoint: "/upload/product-costs",
      description: "Upload product cost information (CSV/Excel)",
      requiredColumns: ["product_name", "cost_per_unit"],
      sampleData: "product_name,cost_per_unit\nPizza Margherita,8.50",
    },
    {
      id: "raws",
      title: "Raw Materials",
      icon: "📦",
      endpoint: "/upload/raw-materials",
      description: "Upload raw materials data (CSV/Excel)",
      requiredColumns: ["item_name", "cost_per_unit", "supplier"],
      sampleData: "item_name,cost_per_unit,supplier\nTomatoes,2.50,Local Farm",
    },
  ];

  const handleFileUpload = async (config, file) => {
    if (!file) return;

    setUploadStates((prev) => ({
      ...prev,
      [config.id]: { loading: true, message: "Uploading...", error: "" },
    }));

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        `https://restaurant-analytics-backend.onrender.com${config.endpoint}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        setUploadStates((prev) => ({
          ...prev,
          [config.id]: {
            loading: false,
            message: result.message || "Upload successful!",
            error: "",
          },
        }));
        updateDataStatus(config.id, true);
      } else {
        throw new Error(result.detail || "Upload failed");
      }
    } catch (error) {
      setUploadStates((prev) => ({
        ...prev,
        [config.id]: {
          loading: false,
          message: "",
          error: error.message,
        },
      }));
      updateDataStatus(config.id, false);
    }
  };

  const downloadSampleData = (config) => {
    const blob = new Blob([config.sampleData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sample_${config.id}_data.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      className="data-upload"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="upload-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <h2>📁 Data Upload Center</h2>
        <p>
          Upload your restaurant data to start generating insights and analytics
        </p>
      </motion.div>

      <motion.div
        className="upload-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {uploadConfigs.map((config, index) => (
          <motion.div
            key={config.id}
            className={`upload-card ${
              dataStatus[config.id] ? "completed" : ""
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="card-header">
              <span className="card-icon">{config.icon}</span>
              <h3>{config.title}</h3>
              {dataStatus[config.id] && (
                <span className="success-badge">✅</span>
              )}
            </div>

            <p className="card-description">{config.description}</p>

            <div className="required-columns">
              <h4>Required Columns:</h4>
              <div className="columns-list">
                {config.requiredColumns.map((col) => (
                  <span key={col} className="column-tag">
                    {col}
                  </span>
                ))}
              </div>
            </div>

            <div className="upload-actions">
              <input
                type="file"
                id={`file-${config.id}`}
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileUpload(config, e.target.files[0])}
                disabled={uploadStates[config.id].loading}
                className="file-input"
              />
              <label htmlFor={`file-${config.id}`} className="file-label">
                {uploadStates[config.id].loading
                  ? "⏳ Uploading..."
                  : "📂 Choose File"}
              </label>

              <button
                onClick={() => downloadSampleData(config)}
                className="sample-btn"
                title="Download sample data format"
              >
                📄 Sample
              </button>
            </div>

            {uploadStates[config.id].loading && (
              <div className="loading-bar">
                <div className="loading-progress"></div>
              </div>
            )}

            {uploadStates[config.id].message && (
              <div className="upload-message success">
                {uploadStates[config.id].message}
              </div>
            )}

            {uploadStates[config.id].error && (
              <div className="upload-message error">
                ❌ {uploadStates[config.id].error}
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        className="upload-tips"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <h3>💡 Upload Tips</h3>
        <ul>
          <li>Supported formats: CSV, Excel (.xlsx, .xls)</li>
          <li>Make sure your data has the required columns listed above</li>
          <li>Sales data is required for basic analytics</li>
          <li>Product costs are needed for profit analysis</li>
          <li>Raw materials data is optional but provides deeper insights</li>
          <li>Download sample files to see the expected format</li>
        </ul>
      </motion.div>
    </motion.div>
  );
}

export default DataUpload;
